import { Badge, Input, Modal } from "antd";
import classNames from "classnames";
import React from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import { SortableHandle } from "react-sortable-hoc";
import { FragmentFieldTypeIcon } from "../../assets/custom_icons/components/fragment";
import {
  withPermissionsToInteract,
  WithPermissionsToInteractReturnType,
} from "../../providers/UserPermissionsProvider";
import { isFunction } from "../../services/utils";
import ActionList, { ActionDef } from "../atoms/ActionList";
import SimpleForm from "../SimpleForm";
import Typography from "../Typography";
import classes from "./index.module.scss";

export interface CustomFieldElementProps extends WithPermissionsToInteractReturnType {
  draggable: boolean;
  active?: boolean;
  hasErrors?: boolean;
  className?: string;
  disabled?: boolean;
  fieldName?: string;
  fieldType?: string;
  relatedFields?: string[];
  fieldId?: string;
  onClone?(newFieldName: string): any;
  onEdit?(): any;
  onInspect?(): any;
  onRemove?(): any;
  onCheckNameAlreadyExist(fieldName: string): boolean;
}

interface ContentDefinitionElementState {
  editing: boolean;
  isCloning: boolean;
}

function getInitialState(): ContentDefinitionElementState {
  return {
    editing: false,
    isCloning: false,
  };
}

const CreateDraggableElement = SortableHandle(({ children }: { children: JSX.Element }) => children);

const labels = defineMessages({
  view: {
    id: "components.DefinitionSchemaEditor.CustomFieldElement.view",
    defaultMessage: "View",
  },
  edit: {
    id: "components.DefinitionSchemaEditor.CustomFieldElement.edit",
    defaultMessage: "Edit",
  },
  clone: {
    id: "components.DefinitionSchemaEditor.CustomFieldElement.clone",
    defaultMessage: "Clone",
  },
  delete: {
    id: "components.DefinitionSchemaEditor.CustomFieldElement.delete",
    defaultMessage: "Delete",
  },
});

class CustomFieldElement extends React.Component<CustomFieldElementProps, ContentDefinitionElementState> {
  public state = getInitialState();

  public createContextualMenuToggleHandler(editing: boolean) {
    return this.props.disabled ? () => void 0 : () => this.setState({ editing });
  }

  public handleClone = (newFieldName: string) => {
    if (isFunction(this.props.onClone) && this.props.hasPermissions) {
      this.createContextualMenuToggleHandler(false)();
      this.props.onClone(newFieldName);
      this.setState({
        isCloning: false,
      });
    }
  };

  public handleEdit = () => {
    if (isFunction(this.props.onEdit)) {
      this.createContextualMenuToggleHandler(false)();
      return this.props.onEdit();
    }
  };

  public handleInspect = () => {
    if (isFunction(this.props.onInspect) && this.props.hasPermissions) {
      this.createContextualMenuToggleHandler(false)();
      return this.props.onInspect();
    }
  };

  public handleRemove = () => {
    if (isFunction(this.props.onRemove) && this.props.hasPermissions) {
      this.createContextualMenuToggleHandler(false)();
      return this.props.onRemove();
    }
  };

  openCloneModal = () => {
    this.setState({
      isCloning: true,
    });
  };

  closeCloneModal = () => {
    this.setState({
      isCloning: false,
    });
  };

  createElementHandle = () => (
    <span
      className={classNames(classes.ContentDefinitionElementHandle, {
        [classes.ContentDefinitionElementDraggable]: this.props.draggable,
      })}
    >
      {this.props.draggable && <FragmentFieldTypeIcon title="Custom Field" />}
    </span>
  );

  createActions = () => {
    const actions = [] as ActionDef[];

    const { hasPermissions } = this.props;

    const viewAction = {
      clickHandler: this.props.onInspect,
      title: labels.view,
      icon: "edit",
      type: "primary",
    } as ActionDef;

    const editAction = {
      clickHandler: this.props.onEdit,
      title: labels.edit,
      icon: "edit",
      type: "primary",
    } as ActionDef;

    actions.push(hasPermissions ? editAction : viewAction);

    if (hasPermissions) {
      actions.push({
        clickHandler: this.openCloneModal,
        title: labels.clone,
        icon: "copy",
        type: "default",
      });
      actions.push({
        clickHandler: this.props.onRemove,
        title: labels.delete,
        icon: "delete",
        type: "danger",
      });
    }

    return <ActionList actions={actions} />;
  };

  public render() {
    const {
      active,
      hasErrors,
      disabled,
      className,
      children,
      fieldName,
      // fieldType,
      relatedFields = [],
      draggable,
      fieldId,
      // fieldDescriptors,
    } = this.props;
    return (
      <div className={classes.ContentDefinitionElementContainer}>
        <div
          className={classNames(
            classes.ContentDefinitionElement,
            {
              [classes.ContentDefinitionElementActive]: active,
              [classes.ContentDefinitionElementDisabled]: disabled,
            },
            className,
          )}
        >
          <div className={classes.ContentDefinitionElementSection}>
            {draggable ? (
              <CreateDraggableElement>{this.createElementHandle()}</CreateDraggableElement>
            ) : (
              this.createElementHandle()
            )}
            <div className={classes.ContentDefinitionElementAvatar}>
              <FragmentFieldTypeIcon title={"Custom Field"} />
            </div>
            <div className={classes.ContentDefinitionElementContent}>
              <Typography variant="h6" gutter={false}>
                {fieldName}
              </Typography>
              {relatedFields.length > 0 && <Typography variant="small">{relatedFields.join(", ")}</Typography>}
            </div>
            {this.createActions()}
          </div>
          {children && (
            <div
              className={classNames(
                classes.ContentDefinitionElementSection,
                classes.ContentDefinitionElementSectionChildren,
              )}
            >
              {children}
            </div>
          )}
        </div>
        <Badge dot={hasErrors} />
        <Modal
          visible={this.state.isCloning}
          footer={null}
          onCancel={this.closeCloneModal}
          title={
            <FormattedMessage id="CustomFieldElement.cloneModal.labels.title" defaultMessage="Insert field name" />
          }
          centered={true}
          destroyOnClose={true}
        >
          <SimpleForm
            layout="horizontal"
            onSubmit={(fields: { name: string }) => this.handleClone(fields.name)}
            fields={[
              {
                id: "name",
                component: <Input />,
                initialValue: `${fieldId}-(copy)`,
                rules: [
                  {
                    required: true,
                    type: "string",
                  },
                  {
                    validator: (rule, value, callback) => {
                      const errors: string[] = [];
                      if (this.props.onCheckNameAlreadyExist(value)) {
                        errors.push(rule.message);
                      }
                      callback(errors);
                    },
                    message: (
                      <FormattedMessage
                        id="CustomFieldElement.cloneModal.labels.nameAlreadyExists"
                        defaultMessage="Name is already in use"
                      />
                    ),
                  },
                  {
                    pattern: /^[a-zA-Z0-9_]+$/,
                  },
                ],
              },
            ]}
          />
        </Modal>
      </div>
    );
  }
}

export default withPermissionsToInteract(CustomFieldElement);
